'use client';

import { posthogCaptureException } from '@lib/analytics/posthog';
import { Button } from '@modules/common/components/button';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { Text } from '@modules/common/components/text';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

type ProductErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function ProductError({ error, reset }: ProductErrorProps) {
  const router = useRouter();

  useEffect(() => {
    Sentry.captureException(error, { tags: { context: 'product_page' } });
    posthogCaptureException(error, { context: 'product_page' });
  }, [error]);

  function handleRetry() {
    router.refresh();
    reset();
  }

  return (
    <section className="flex flex-col items-center justify-center min-h-[70vh] tablet:min-h-[88vh] gap-4 py-16 text-center">
      <h1 className="font-heading nw-typography-heading-lg font-light text-fg-neutral-strong text-center">
        Something went wrong
      </h1>
      <Text className="text-ui-fg-subtle max-w-md">
        We couldn&apos;t load this product. Please try again or browse our other
        products.
      </Text>
      <div className="flex gap-3">
        <Button variant="secondary" onClick={handleRetry}>
          Try again
        </Button>
        <Button asChild>
          <LocalizedClientLink href="/search">
            Browse products
          </LocalizedClientLink>
        </Button>
      </div>
    </section>
  );
}
